import { useNotification } from "@context/NotificationContext";
import { claimTaskByVesselId, unclaimTaskByVesselId } from "@services/tasksApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import type { AxiosError } from "axios";

type ClaimTaskVariables = {
  service: string;
  vesselId: string;

  successTitle?: string;
  successDescription?: string;
  errorTitle: string;

  action: "claim" | "unclaim";
};

export const useClaimTask = ({ handleSuccess }: { handleSuccess?: () => void }) => {
  const { notify } = useNotification();

  const mutation = useMutation({
    mutationFn: async (vars: ClaimTaskVariables) => {
      const { service, vesselId, action } = vars;

      return runTransaction(
        {
          module: "vessel_registration",
          screen: "VesselStatus",
          action: action === "claim" ? "claim_vessel_registration" : "unclaim_vessel_registration",
          entityType: "vessel",
        },
        (transactionId) =>
          action === "claim"
            ? claimTaskByVesselId(service, vesselId, ...(transactionId ? [transactionId] : []))
            : unclaimTaskByVesselId(service, vesselId, ...(transactionId ? [transactionId] : [])),
      );
    },

    onSuccess: (_, variables) => {
      const { successTitle, successDescription } = variables;
      if (successTitle)
        notify({
          title: successTitle,
          description: successDescription,
          type: "success",
        });

      handleSuccess?.();
    },

    onError: (error: AxiosError<{ detail?: string }>, vars) => {
      const { errorTitle } = vars;

      notify({
        title: errorTitle,
        description: error.response?.data?.detail ?? error.message,
        type: "error",
      });
    },
  });

  return mutation;
};
